/** Add / edit a partner, including photo upload. */
import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom'
import { PageHeader } from '../App'
import { api, useApi } from '../lib/api'
import { CountrySelect, EnumSelect } from '../components/Select'
import { AiAssist } from '../components/AiPanel'
import { ImageUpload, MediaImage } from '../components/ImageUpload'
import {
  Card, Field, Notice, Spinner, Badge, Loading, ErrorState, ConfirmModal,
  Switch, useToast, fmtInt,
} from '../components/ui'
import { useRelabel, useCode, useCodeInput, usePartnerTerm } from '../lib/terms'
import { FUND_TYPES } from '../../shared/fundTypes.js'

const stripTags = (s) => String(s || '').replace(/<[^>]+>/g, '').trim()

export default function PartnerForm({ user, mode }) {
  const code = useCode()
  const uncode = useCodeInput()
  const { codePrefix: prefix } = usePartnerTerm()
  const t = useRelabel()
  const { id } = useParams()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const toast = useToast()
  const isEdit = mode === 'edit'
  const query = useApi(['partner', id], `partners/${id}`, { enabled: isEdit })

  const [form, setForm] = useState({
    s4ca_code: '',
    display_name: '',
    country_id: null,
    initial_year: '',
    staff_partners: '',
    non_staff_partners: '',
    description: '',
    status: 'active',
    /* Staff unless the page that opened this form said otherwise — "+ New" on Training
       Schools and on Projects carries `?fund_type=`, so a record created from one of those
       pages is of that kind and appears on the page it was created from. Read once, as the
       initial state, so a reader who then changes the select is not overruled by the URL. */
    fund_type: FUND_TYPES.includes(params.get('fund_type')) ? params.get('fund_type') : 'staff',
    photo_media_id: null,
    donate_url: '',
  })
  const [loaded, setLoaded] = useState(!isEdit)
  const [busy, setBusy] = useState(false)
  const [errors, setErrors] = useState({})
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [cascade, setCascade] = useState(false)

  const set = (patch) => setForm((f) => ({ ...f, ...patch }))

  useEffect(() => {
    if (!isEdit || !query.data || loaded) return
    const p = query.data.partner
    setForm({
      s4ca_code: p.s4ca_code,
      display_name: p.display_name ?? '',
      country_id: p.country_id,
      initial_year: p.initial_year ?? '',
      staff_partners: p.staff_partners ?? '',
      non_staff_partners: p.non_staff_partners ?? '',
      description: p.description ?? '',
      status: p.status,
      fund_type: p.fund_type ?? 'staff',
      photo_media_id: p.photo_media_id,
      donate_url: p.donate_url ?? '',
    })
    setLoaded(true)
  }, [isEdit, query.data, loaded])

  const canWrite = isEdit ? user.caps.update : user.caps.create

  const validate = () => {
    const e = {}
    /* `entered`, not `code`: this component holds a `code` of its own now — the hook that
       draws a stored code in the installation's prefix — and a local of the same name here
       would shadow it for anything added to this function later. */
    const entered = form.s4ca_code.trim()
    if (!entered) e.s4ca_code = 'An S4CA code is required'
    /* Either prefix. The field folds what was typed back to the stored spelling before it
       gets here, so this sees S4CA — but somebody who typed the archive's own prefix while
       the installation shows another should not be told it is the wrong shape. */
    else if (!new RegExp(`^(?:S4CA|${prefix})-\\d{3,}$`, 'i').test(entered)) {
      e.s4ca_code = 'Expected the form S4CA-123'
    }
    if (!form.display_name.trim()) e.display_name = 'A name is required'
    if (form.initial_year && !/^\d{4}$/.test(String(form.initial_year))) {
      e.initial_year = 'Enter a four digit year'
    }
    // Both head counts, the same rule the registry file is held to. The Worker refuses
    // anything else as well; this is the earlier, kinder refusal.
    for (const k of ['staff_partners', 'non_staff_partners']) {
      if (form[k] !== '' && !/^\d{1,3}$/.test(String(form[k]))) {
        e[k] = 'Enter a whole number, or leave it empty'
      }
    }
    // The profile turns this into a link somebody clicks, so it has to be one. The
    // Worker refuses anything else as well — this is only the earlier, kinder refusal.
    if (form.donate_url.trim() && !/^https?:\/\/\S+$/i.test(form.donate_url.trim())) {
      e.donate_url = 'Enter a full address starting with http:// or https://'
    }
    setErrors(e)
    return !Object.keys(e).length
  }

  const save = async () => {
    if (!validate()) {
      toast.error('Please fix the highlighted fields')
      return
    }
    setBusy(true)
    try {
      const body = { ...form }
      if (isEdit) {
        await api(`partners/${id}`, { method: 'PUT', body })
        toast.ok('Partner unit saved')
        navigate(`/partners/${id}`)
      } else {
        const res = await api('partners', { method: 'POST', body })
        toast.ok('Partner unit created')
        navigate(`/partners/${res.id}`)
      }
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    setBusy(true)
    try {
      await api(`partners/${id}${cascade ? '?cascade=1' : ''}`, { method: 'DELETE' })
      toast.ok('Partner unit deleted')
      navigate('/partners')
    } catch (err) {
      toast.error(err)
      setBusy(false)
    }
  }

  if (isEdit && query.isLoading) {
    return (
      <PageHeader title="Edit Partner Unit">
        <Loading />
      </PageHeader>
    )
  }
  if (isEdit && query.isError) {
    return (
      <PageHeader title="Edit Partner Unit">
        <ErrorState error={query.error} retry={query.refetch} />
      </PageHeader>
    )
  }

  const newsletterCount = query.data?.partner?.newsletter_count ?? 0

  return (
    <PageHeader
      title={isEdit ? `Edit ${code(form.s4ca_code)}` : 'New Partner Unit'}
      actions={
        <>
          {isEdit && (
            <Link className="btn btn-sm" to={`/partners/${id}`}>
              View
            </Link>
          )}
          <Link className="btn btn-sm" to="/partners">
            Cancel
          </Link>
        </>
      }
    >
      {!canWrite && (
        <Notice tone="warn" icon="lock" title="Read-only">
          Your role ({user.roleLabel}) cannot {isEdit ? 'edit' : 'create'} partner units.
        </Notice>
      )}

      <div className="col">
        <Card title="Identity">
          <div className="field-row">
            {/* Shown with the installation's prefix, like every other code in the archive,
                and folded back to the stored spelling on the way in — see codeInput. */}
            <Field label="S4CA Code" required error={errors.s4ca_code} hint="e.g. S4CA-123">
              <input
                className="input mono"
                value={code(form.s4ca_code)}
                onChange={(e) => set({ s4ca_code: uncode(e.target.value) })}
                disabled={!canWrite}
                placeholder={t('S4CA-')}
              />
            </Field>
            <Field label="Name" required error={errors.display_name} hint="Initials as used in the archive">
              <input
                className="input"
                value={form.display_name}
                onChange={(e) => set({ display_name: e.target.value })}
                disabled={!canWrite}
              />
            </Field>
            <Field label="Country" htmlFor="pf-country">
              <CountrySelect
                inputId="pf-country"
                value={form.country_id}
                onChange={(v) => set({ country_id: v })}
                isDisabled={!canWrite}
              />
            </Field>
            <Field label="Initial year" error={errors.initial_year}>
              <input
                className="input"
                type="number"
                value={form.initial_year}
                onChange={(e) => set({ initial_year: e.target.value })}
                disabled={!canWrite}
                placeholder="2005"
              />
            </Field>
            {/* The two head-count columns from the registry file. Editable here for the
                same reason the year and the country are: a unit the file has not reached
                — one somebody made by hand, or a stub the updates invented — would
                otherwise have no way of ever being counted. An import overwrites both,
                as it overwrites every other field it owns. Empty means uncounted, which
                is not the same as nobody: see migration 0026. */}
            <Field
              label="Staff partners"
              htmlFor="pf-staff"
              error={errors.staff_partners}
              hint="From the registry file. Both kinds count as partners here"
            >
              <input
                id="pf-staff"
                className="input"
                type="number"
                min="0"
                value={form.staff_partners}
                onChange={(e) => set({ staff_partners: e.target.value })}
                disabled={!canWrite}
                placeholder="—"
              />
            </Field>
            <Field
              label="Non-staff partners"
              htmlFor="pf-non-staff"
              error={errors.non_staff_partners}
              hint="Two together is usually a married couple serving as one unit"
            >
              <input
                id="pf-non-staff"
                className="input"
                type="number"
                min="0"
                value={form.non_staff_partners}
                onChange={(e) => set({ non_staff_partners: e.target.value })}
                disabled={!canWrite}
                placeholder="—"
              />
            </Field>
            <Field
              label="Donate link"
              error={errors.donate_url}
              hint="Becomes the Donate button on the profile"
            >
              <input
                className="input"
                type="url"
                value={form.donate_url}
                onChange={(e) => set({ donate_url: e.target.value })}
                disabled={!canWrite}
                placeholder="https://"
              />
            </Field>
            {/* Which of the three Register pages this record belongs on. Beside Status
                because they are the two fields that decide where it is *listed* rather
                than what it says — and the note is there because changing this moves the
                record off the page whoever is reading it came from. */}
            <Field label="Fund Type" hint="Decides which Register page this record appears on" htmlFor="p-fund-type">
              <EnumSelect
                inputId="p-fund-type"
                value={form.fund_type}
                onChange={(v) => set({ fund_type: v })}
                options={[
                  { value: 'staff', label: 'Staff — Partner Unit' },
                  { value: 'training', label: 'Training School' },
                  { value: 'project', label: 'Project' },
                ]}
                isClearable={false}
                isDisabled={!canWrite}
              />
            </Field>
            <Field label="Status" htmlFor="p-status">
              <EnumSelect
                inputId="p-status"
                value={form.status}
                onChange={(v) => set({ status: v })}
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                  { value: 'alumni', label: 'Alumni' },
                ]}
                isClearable={false}
                isDisabled={!canWrite}
              />
            </Field>
          </div>
        </Card>

        <Card
          title="Description"
          sub="Shown on the partner unit profile"
          actions={
            user.caps.useAi && canWrite && form.description ? (
              <AiAssist
                fieldKey="partners.description"
                fieldLabel="Description"
                value={form.description}
                onAccept={(text) => set({ description: stripTags(text) })}
                entity="partner"
                entityId={isEdit ? Number(id) : null}
                asHtml={false}
              />
            ) : null
          }
        >
          <textarea
            className="textarea"
            value={form.description}
            onChange={(e) => set({ description: e.target.value })}
            disabled={!canWrite}
            rows={5}
          />
        </Card>

        {isEdit && user.caps.uploadMedia && (
          <Card title="Photo" sub="Automatically resized below 300 KB before it is stored">
            <div className="grid grid-2">
              <ImageUpload
                kind="partner_photo"
                partnerId={Number(id)}
                maxBytes={user.maxImageBytes}
                onUploaded={(res) => {
                  set({ photo_media_id: res.id })
                  query.refetch()
                }}
                label={t('Upload a partner unit photo')}
              />
              <div>
                <div className="media-grid">
                  {query.data?.images?.map((img) => (
                    <div className="media-item" key={img.id}>
                      <MediaImage id={img.id} alt={img.alt_text} />
                      <div className="media-meta">
                        <div className="truncate subtle">{img.alt_text || img.filename}</div>
                        <div className="row row-tight" style={{ marginTop: 4 }}>
                          {form.photo_media_id === img.id ? (
                            <Badge tone="ok">profile photo</Badge>
                          ) : (
                            <button
                              className="btn btn-sm"
                              onClick={() => set({ photo_media_id: img.id })}
                              disabled={!canWrite}
                            >
                              Use as photo
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {!query.data?.images?.length && <p className="small subtle">No images uploaded yet.</p>}
              </div>
            </div>
          </Card>
        )}

        <div className="row" style={{ paddingBottom: 24 }}>
          <button className="btn btn-primary btn-lg" onClick={save} disabled={busy || !canWrite}>
            {busy ? <Spinner /> : isEdit ? 'Save changes' : t('Create partner unit')}
          </button>
          <span className="spacer" />
          {isEdit && user.caps.delete && (
            <button className="btn btn-danger" onClick={() => setConfirmDelete(true)} disabled={busy}>
              {t('Delete partner unit')}
            </button>
          )}
        </div>
      </div>

      <ConfirmModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={remove}
        busy={busy}
        confirmLabel={newsletterCount && !cascade ? 'Cannot delete' : t('Delete partner unit')}
        title={`Delete ${code(form.s4ca_code)}?`}
        body={
          <div className="col">
            {newsletterCount > 0 ? (
              <>
                <Notice tone="danger" icon="warning" title={`This partner unit has ${fmtInt(newsletterCount)} updates`}>
                  Deleting the partner unit deletes every one of their updates. There is no undo.
                </Notice>
                <Switch
                  checked={cascade}
                  onChange={setCascade}
                  label={`Yes, also delete ${fmtInt(newsletterCount)} updates`}
                />
              </>
            ) : (
              <p>
                <strong>{code(form.s4ca_code)}</strong> has no updates and will be removed.
              </p>
            )}
          </div>
        }
      />
    </PageHeader>
  )
}
